import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Loader2, CreditCard, Check, Crown, Sparkles, ShieldCheck, ArrowRight, X, ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fireGoogleAdsConversion } from "@/lib/googleAds";

const CYCLE_LABEL = { monthly: "Monthly", annual: "Annual" };
const PRICE_SUFFIX = { monthly: "/mo", annual: "/yr" };

export default function Billing() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [billingProvider, setBillingProvider] = useState("disabled");
  const [usage, setUsage] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(null);   // plan_id being purchased / reserved
  const [reserved, setReserved] = useState({});  // {[planId]: true} once user reserved
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState("");
  const [pollFailed, setPollFailed] = useState(null);  // session_id if timeout
  const [paddleReady, setPaddleReady] = useState(false);
  const [paddleConfig, setPaddleConfig] = useState(null);

  // ---- Load catalog + current usage
  const refresh = async () => {
    try {
      const [p, u] = await Promise.all([
        api.get("/billing/plans"),
        api.get("/billing/usage"),
      ]);
      setPlans(p.data.plans);
      setBillingProvider((p.data.billing_provider || "disabled").toLowerCase());
      setUsage(u.data);
    } catch (err) {
      toast.error("Couldn't load billing", { description: formatApiError(err) });
    }
  };
  useEffect(() => { refresh(); }, []);

  // ---- Bootstrap Paddle.js once when the provider is Paddle.
  useEffect(() => {
    if (billingProvider !== "paddle") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/billing/paddle/config");
        if (cancelled) return;
        setPaddleConfig(data);

        // Lazy-load Paddle.js from CDN (idempotent).
        const SRC = "https://cdn.paddle.com/paddle/v2/paddle.js";
        if (!document.querySelector(`script[src="${SRC}"]`)) {
          await new Promise((resolve, reject) => {
            const s = document.createElement("script");
            s.src = SRC; s.async = true;
            s.onload = resolve;
            s.onerror = () => reject(new Error("Failed to load Paddle.js"));
            document.head.appendChild(s);
          });
        }
        if (cancelled) return;
        const Paddle = window.Paddle;
        if (!Paddle) throw new Error("Paddle global missing after script load");
        if (data.environment !== "live") {
          Paddle.Environment.set("sandbox");
        }
        Paddle.Initialize({
          token: data.client_token,
          eventCallback: (ev) => {
            if (ev?.name === "checkout.completed") {
              // Fire Google Ads "Subscribe" conversion. Paddle's transaction
              // id is the idempotency key; value + currency populate ROAS.
              const totalStr = ev?.data?.totals?.total ?? ev?.data?.total;
              const numericTotal = totalStr !== undefined ? Number(totalStr) : undefined;
              fireGoogleAdsConversion("subscribe", {
                transactionId: ev?.data?.transaction_id || ev?.data?.id,
                value: Number.isFinite(numericTotal) ? numericTotal : undefined,
                currency: ev?.data?.currency_code
                  || ev?.data?.currency
                  || ev?.data?.totals?.currency_code
                  || "USD",
              });

              // Webhook does the actual provisioning — we just poll usage
              // a few times so the UI catches up the moment the webhook lands.
              toast.success("Payment received", {
                description: "Your upgrade is being finalised — this takes a few seconds.",
                duration: 6000,
              });
              const start = Date.now();
              const startPlan = usage?.plan;
              let synced = false;
              const tick = async () => {
                const fresh = await api.get("/billing/usage").then(r => r.data).catch(() => null);
                if (fresh) setUsage(fresh);
                const flipped = fresh && fresh.plan && fresh.plan !== startPlan;
                if (flipped) return;
                // Safety net: if the webhook hasn't landed after ~15s,
                // ask the backend to sync directly with Paddle's API.
                if (!synced && Date.now() - start > 15000) {
                  synced = true;
                  try {
                    await api.post("/billing/paddle/sync-me");
                    // Immediately poll once more so the UI catches up.
                    const fresh2 = await api.get("/billing/usage").then(r => r.data);
                    setUsage(fresh2);
                  } catch (err) {
                    console.warn("Paddle sync-me failed:", err);
                  }
                }
                if (Date.now() - start < 30000) setTimeout(tick, 2000);
              };
              tick();
            } else if (ev?.name === "checkout.closed" && ev?.data?.status !== "completed") {
              setBusy(null);
            }
          },
        });
        setPaddleReady(true);
      } catch (err) {
        console.warn("Paddle init failed:", err);
        // Don't toast — the user can still reserve founder pricing.
      }
    })();
    return () => { cancelled = true; };
  }, [billingProvider]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Manual "I paid — refresh now" handler
  const manualRefresh = async (sid) => {
    setPollFailed(null);
    setPolling(true);
    setPollMsg("Re-checking with Stripe…");
    try {
      const { data } = await api.get(`/billing/status/${sid}`);
      if (data.payment_status === "paid" && data.applied) {
        toast.success(`Welcome to ${data.plan === "pro" ? "Pro" : "Business"}!`,
          { description: "Your workspace is upgraded." });
        setPolling(false);
        setParams({});
        await refresh();
      } else if (data.status === "expired" || data.payment_status === "unpaid") {
        setPolling(false);
        setPollFailed(sid);
        setPollMsg("");
        toast.warning("Payment not confirmed yet", {
          description: "If you completed the payment, please wait a moment and click 'Refresh' again. If the issue persists, contact support.",
          duration: 12000,
        });
      } else {
        setPollFailed(sid);
        setPolling(false);
      }
    } catch (err) {
      setPolling(false);
      setPollFailed(sid);
      toast.error("Status check failed", { description: formatApiError(err) });
    }
  };

  // ---- Handle return-from-Stripe (poll status with smart backoff)
  useEffect(() => {
    const sid = params.get("session_id");
    const status = params.get("status");
    if (!sid || status === "cancel") {
      if (status === "cancel") {
        toast.info("Checkout cancelled", { description: "No changes were made." });
        setParams({});
      }
      return;
    }
    setPolling(true);
    setPollMsg("Finalizing your upgrade…");
    let attempts = 0;
    // 30 polls @ 3s = 90s total budget. Plenty for Stripe to settle.
    const maxAttempts = 30;
    const intervalMs = 3000;
    let cancelled = false;

    const tick = async () => {
      attempts += 1;
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sid}`);
        if (cancelled) return;
        if (data.payment_status === "paid" && data.applied) {
          setPolling(false);
          toast.success(`Welcome to ${data.plan === "pro" ? "Pro" : "Business"}!`,
            { description: "Your workspace is upgraded. Enjoy the new limits." });
          setParams({});
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setPolling(false);
          setPollFailed(sid);
          toast.error("Checkout expired", { description: "The session timed out. Please start a new checkout." });
          setParams({});
          return;
        }
        // Update friendly progress messages
        if (attempts <= 3) setPollMsg("Finalizing your upgrade…");
        else if (attempts <= 10) setPollMsg("Waiting for payment confirmation from Stripe…");
        else if (attempts <= 20) setPollMsg("Almost there — this is taking a little longer than usual…");
        else setPollMsg("Still confirming with Stripe… hold tight.");

        if (attempts >= maxAttempts) {
          setPolling(false);
          setPollFailed(sid);
          setPollMsg("");
          return;
        }
        setTimeout(tick, intervalMs);
      } catch (err) {
        if (cancelled) return;
        setPolling(false);
        setPollFailed(sid);
        toast.error("Status check failed", { description: formatApiError(err) });
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [params]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ---- Upgrade flow (live billing only)
  const upgrade = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan: planId,
        cycle,
        origin_url: window.location.origin,
      });

      // Paddle overlay mode — no server-side redirect URL. We open the
      // Paddle.js overlay client-side with the right price + customData.
      if (data?.provider === "paddle" && data?.mode === "overlay") {
        if (!paddleReady || !paddleConfig || !window.Paddle) {
          toast.error("Checkout is still warming up — please try again in a sec.");
          setBusy(null);
          return;
        }
        const priceId = paddleConfig.prices?.[planId]?.[cycle];
        if (!priceId) {
          toast.error("Couldn't open checkout", {
            description: `Missing Paddle price for ${planId} / ${cycle}.`,
          });
          setBusy(null);
          return;
        }
        window.Paddle.Checkout.open({
          items: [{ priceId, quantity: 1 }],
          // Pre-fill customer email from the authenticated session so the
          // Paddle overlay locks the checkout to the same identity as the
          // AgentFlow account. Prevents "we can't find your subscription"
          // when the webhook fires with a customer email that doesn't
          // match any user in our DB.
          customer: usage?.user_email ? { email: usage.user_email } : undefined,
          customData: {
            workspace_id: usage?.workspace_id,
            user_email: usage?.user_email,
            plan: planId,
            cycle,
          },
        });
        // setBusy(null) happens on checkout.closed in the event callback.
        return;
      }

      // Legacy Stripe redirect path (kept for emergencies).
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Unexpected checkout response.");
    } catch (err) {
      if (err?.response?.status === 503) {
        // Billing is disabled — fall back to reservation flow.
        await reservePlan(planId);
      } else {
        toast.error("Couldn't start checkout", { description: formatApiError(err) });
        setBusy(null);
      }
    }
  };

  // ---- Reserve founder pricing (when paid billing is disabled)
  const reservePlan = async (planId) => {
    setBusy(planId);
    try {
      await api.post("/billing/reserve", { plan: planId, cycle });
      setReserved((r) => ({ ...r, [planId]: true }));
      toast.success("Founder pricing reserved", {
        description: "We'll email you the moment Paddle goes live — your price is locked in.",
        duration: 6000,
      });
    } catch (err) {
      toast.error("Couldn't save reservation", { description: formatApiError(err) });
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!confirm("Cancel your subscription at the end of the current period?")) return;
    try {
      const { data } = await api.post("/billing/cancel");
      // Envelope: {ok, code?} — always HTTP 200 for expected Paddle failures
      // (network, 5xx) so Cloudflare doesn't swallow our JSON response.
      if (data && data.ok === false) {
        const code = data.code || "paddle_cancel_unavailable";
        if (code === "subscription_not_found") {
          toast.error("Subscription not found in billing", {
            description: "Your billing record is out of sync. Please open Manage Billing to cancel directly, or contact support.",
            duration: 10000,
          });
        } else {
          toast.error("Cancellation failed", {
            description: "We couldn't reach our billing provider. Please try again in a moment or email support@agentflow.ventures.",
          });
        }
        return;
      }
      toast.success("Cancellation scheduled");
      refresh();
    } catch (err) {
      toast.error("Cancellation failed", { description: formatApiError(err) });
    }
  };
  const undoCancel = async () => {
    try {
      const { data } = await api.post("/billing/cancel/undo");
      if (data && data.ok === false) {
        const code = data.code || "paddle_resume_unavailable";
        if (code === "subscription_not_found") {
          toast.error("Subscription no longer active", {
            description: "This subscription has already ended and can't be reactivated. Start a fresh upgrade instead.",
            duration: 10000,
          });
        } else {
          toast.error("Couldn't update", {
            description: "We couldn't reach our billing provider. Please try again in a moment.",
          });
        }
        return;
      }
      toast.success("Subscription reinstated");
      refresh();
    } catch (err) {
      toast.error("Couldn't update", { description: formatApiError(err) });
    }
  };

  // ---- Open Paddle customer portal (self-service card update, invoices, cancel)
  const [portalBusy, setPortalBusy] = useState(false);
  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/paddle/portal-session");
      // Envelope: {ok, code?, overview_url?, subscription_url?} — always 200.
      // We branch on `ok` rather than HTTP status because Emergent's edge
      // swaps 5xx responses for its own HTML error page, eating our JSON.
      if (!data.ok) {
        const code = data.code || "paddle_portal_unavailable";
        if (code === "no_paddle_customer") {
          toast.info("Nothing to manage yet", {
            description: "The billing portal opens after your first paid upgrade.",
          });
        } else if (code === "stale_paddle_customer") {
          toast.error("Your test-environment billing was cleared", {
            description:
              "This workspace's Paddle account came from our sandbox and doesn't exist in live billing. Please start a fresh upgrade — takes ~1 minute.",
            duration: 12000,
          });
          refresh();
        } else if (code === "billing_not_configured") {
          toast.error("Billing portal unavailable", {
            description: "Billing isn't fully configured on this environment. Please contact support.",
          });
        } else {
          toast.error("Couldn't open billing portal", {
            description: "We couldn't reach our billing provider. Please try again in a moment or email support@agentflow.ventures.",
          });
        }
        return;
      }
      // Prefer the subscription-scoped deep link when available so the user
      // lands directly on their own subscription. Fall back to overview.
      const url = data.subscription_url || data.overview_url;
      if (!url) throw new Error("Paddle didn't return a portal URL.");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error("Couldn't open billing portal", { description: formatApiError(err) });
    } finally {
      setPortalBusy(false);
    }
  };

  if (!plans || !usage) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  }

  const currentPlan = usage.plan;
  const billingDisabled = billingProvider !== "stripe" && billingProvider !== "paddle";
  const pct = Math.min(100, Math.round((usage.scans_used / Math.max(usage.scan_limit, 1)) * 100));
  const overage = usage.scans_used > usage.scan_limit;

  return (
    <div className="space-y-10" data-testid="billing-page">
      {/* Header */}
      <div>
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Billing</p>
        <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">
          Plans & usage
        </h1>
        <p className="mt-1.5 text-sm text-slate-600">
          Pick the plan that fits your scanning volume. Cancel anytime.
        </p>
      </div>

      {polling && (
        <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 flex items-center gap-4" data-testid="checkout-polling">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900 text-sm">{pollMsg || "Finalizing your upgrade…"}</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Stripe sometimes takes 30–60 seconds to confirm. Don't refresh or close this page.
            </div>
          </div>
        </div>
      )}

      {pollFailed && !polling && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-4" data-testid="checkout-stalled">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900 text-sm">Payment confirmation is taking longer than expected</div>
            <p className="mt-1 text-xs text-slate-700">
              If your card was charged, the upgrade will appear within a couple of minutes.
              Click <strong>Refresh status</strong> below to check again. If nothing changes
              after a few attempts, your payment may not have completed — please contact
              support and reference session ID <code className="bg-white px-1.5 py-0.5 rounded text-[11px] border border-amber-200">{pollFailed.slice(-12)}</code>.
            </p>
          </div>
          <Button
            onClick={() => manualRefresh(pollFailed)}
            className="shrink-0 rounded-md bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="checkout-refresh-button"
          >
            Refresh status
          </Button>
        </div>
      )}

      {billingDisabled && (
        <div
          className="rounded-2xl border border-blue-200 bg-blue-50 p-5 flex items-start gap-4"
          data-testid="billing-disabled-banner"
        >
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900 text-sm">
              Checkout temporarily unavailable
            </div>
            <p className="mt-1 text-xs text-slate-700 leading-relaxed">
              We can't reach our payment processor right now. Please try again
              in a few minutes, or{" "}
              <a
                href="mailto:support@agentflow.ventures"
                className="text-blue-600 font-medium hover:underline"
              >
                contact support
              </a>
              {" "}if it persists. Your Free plan is still fully functional in the meantime.
            </p>
          </div>
        </div>
      )}

      {/* Current usage */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="usage-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
              Current plan
            </div>
            <div className="mt-1 flex items-center gap-3">
              <h2 className="font-display text-2xl font-semibold text-slate-900" data-testid="current-plan-name">
                {usage.plan_name}
              </h2>
              {currentPlan !== "free" && (
                <span className="rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-semibold px-2.5 py-1">
                  Active
                </span>
              )}
              {usage.cancel_at_period_end && (
                <span className="rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold px-2.5 py-1">
                  Cancels at period end
                </span>
              )}
            </div>
            {usage.plan_current_period_end && currentPlan !== "free" && (
              <p className="mt-1 text-xs text-slate-500" data-testid="plan-period-end">
                {usage.cancel_at_period_end ? "Access until " : "Renews "}
                {new Date(usage.plan_current_period_end).toLocaleDateString()}
              </p>
            )}
          </div>
          {currentPlan !== "free" && (
            <div className="flex gap-2 flex-wrap justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={openPortal}
                disabled={portalBusy}
                className="rounded-md inline-flex items-center gap-1.5"
                data-testid="manage-billing"
                title="Update card, view invoices, or cancel from Paddle's secure portal"
              >
                {portalBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ExternalLink className="h-3.5 w-3.5" />
                )}
                Manage billing
              </Button>
              {usage.cancel_at_period_end ? (
                <Button size="sm" onClick={undoCancel} className="rounded-md bg-blue-600 hover:bg-blue-700 text-white" data-testid="undo-cancel">
                  Reactivate
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={cancel} className="rounded-md" data-testid="cancel-subscription">
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="mt-6">
          <div className="flex items-baseline justify-between">
            <div className="text-sm text-slate-600">
              Scans this month
            </div>
            <div className="text-sm font-mono text-slate-900" data-testid="usage-numbers">
              {usage.scans_used} / {usage.scan_limit}
              {currentPlan === "free" && (
                <span className="ml-1.5 text-xs text-slate-500">(+1 grace)</span>
              )}
            </div>
          </div>
          <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className={"h-full transition-all " + (overage ? "bg-rose-500" : pct > 80 ? "bg-amber-500" : "bg-blue-600")}
              style={{ width: `${Math.min(100, pct)}%` }}
              data-testid="usage-bar"
            />
          </div>
          {overage && (
            <p className="mt-2 text-xs text-rose-600">
              You've hit your monthly scan limit. Upgrade below to keep scanning.
            </p>
          )}
        </div>

        {usage.chat_message_limit > 0 && (
          <div className="mt-6 pt-6 border-t border-slate-100" data-testid="chat-usage-row">
            <div className="flex items-baseline justify-between">
              <div className="text-sm text-slate-600">
                AI Chat messages this month
              </div>
              <div className="text-sm font-mono text-slate-900" data-testid="chat-usage-numbers">
                {usage.chat_messages_used} / {usage.chat_message_limit}
              </div>
            </div>
            <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
              {(() => {
                const cPct = Math.min(100, Math.round((usage.chat_messages_used / Math.max(usage.chat_message_limit, 1)) * 100));
                const cTone = usage.chat_messages_remaining <= 0
                  ? "bg-rose-500"
                  : cPct >= 80 ? "bg-amber-500" : "bg-blue-600";
                return (
                  <div
                    className={"h-full transition-all " + cTone}
                    style={{ width: `${cPct}%` }}
                    data-testid="chat-usage-bar"
                  />
                );
              })()}
            </div>
            {usage.chat_messages_remaining <= 0 ? (
              <p className="mt-2 text-xs text-rose-600">
                You've used all chat messages this month. {currentPlan === "pro"
                  ? "Upgrade to Business for 2,000 / month."
                  : "Limit resets on the 1st."}
              </p>
            ) : usage.chat_messages_remaining <= 30 ? (
              <p className="mt-2 text-xs text-amber-700">
                Only {usage.chat_messages_remaining} chat messages left this month.
              </p>
            ) : null}
          </div>
        )}
      </div>

      {/* Cycle toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" data-testid="cycle-toggle">
          {["monthly", "annual"].map((c) => (
            <button
              key={c}
              onClick={() => setCycle(c)}
              data-testid={`cycle-${c}`}
              className={
                "px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition " +
                (cycle === c ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900")
              }
            >
              {CYCLE_LABEL[c]}
              {c === "annual" && (
                <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold tracking-normal normal-case">
                  Save 17%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Plan cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const price = cycle === "annual" ? p.price_annual_usd : p.price_monthly_usd;
          const isCurrent = currentPlan === p.id;
          const featured = p.id === "pro";
          return (
            <div
              key={p.id}
              data-testid={`plan-card-${p.id}`}
              className={
                "rounded-2xl border p-6 shadow-sm relative " +
                (featured
                  ? "border-slate-900 bg-slate-900 text-white"
                  : isCurrent
                    ? "border-blue-300 bg-blue-50/40"
                    : "border-slate-200 bg-white")
              }
            >
              {featured && (
                <span className="absolute -top-3 right-5 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1">
                  Most popular
                </span>
              )}
              {billingDisabled && p.id !== "free" && (
                <span
                  className={"absolute -top-3 left-5 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 " + (featured ? "bg-amber-400 text-slate-900" : "bg-blue-50 text-blue-700 border border-blue-200")}
                  data-testid={`plan-card-${p.id}-beta-badge`}
                >
                  Beta · Founder pricing
                </span>
              )}
              <div className="flex items-center gap-2">
                {p.id === "free" && <ShieldCheck className={"h-4 w-4 " + (featured ? "text-blue-300" : "text-slate-400")} />}
                {p.id === "pro" && <Sparkles className="h-4 w-4 text-amber-400" />}
                {p.id === "business" && <Crown className={"h-4 w-4 " + (featured ? "text-amber-300" : "text-amber-500")} />}
                <div className={"font-display text-lg font-semibold " + (featured ? "text-white" : "text-slate-900")}>
                  {p.name}
                </div>
              </div>
              <div className="mt-3 flex items-baseline gap-1">
                <div className={"font-display text-4xl font-semibold " + (featured ? "text-white" : "text-slate-900")}>
                  ${price === 0 ? 0 : price}
                </div>
                <div className={"text-sm " + (featured ? "text-slate-400" : "text-slate-500")}>
                  {PRICE_SUFFIX[cycle]}
                </div>
              </div>
              {cycle === "annual" && p.id !== "free" && (
                <div className={"text-xs mt-1 " + (featured ? "text-emerald-300" : "text-emerald-600")}>
                  Save ${(p.price_monthly_usd * 12 - p.price_annual_usd).toFixed(0)} vs monthly
                </div>
              )}
              <ul className={"mt-5 space-y-2 text-sm " + (featured ? "text-slate-200" : "text-slate-700")}>
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className={"h-4 w-4 mt-0.5 shrink-0 " + (featured ? "text-emerald-400" : "text-emerald-600")} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {isCurrent ? (
                  <Button disabled className="w-full rounded-lg bg-slate-200 text-slate-500 cursor-not-allowed">
                    Current plan
                  </Button>
                ) : p.id === "free" ? (
                  <Button disabled className="w-full rounded-lg bg-slate-100 text-slate-400 cursor-not-allowed">
                    {currentPlan === "free" ? "Current plan" : "Downgrades at period end"}
                  </Button>
                ) : billingDisabled ? (
                  reserved[p.id] ? (
                    <Button
                      disabled
                      data-testid={`reserved-${p.id}`}
                      className="w-full rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-not-allowed"
                    >
                      <Check className="h-4 w-4 mr-2" /> Reservation saved
                    </Button>
                  ) : (
                    <Button
                      onClick={() => reservePlan(p.id)}
                      disabled={busy !== null}
                      data-testid={`reserve-${p.id}`}
                      className={
                        "w-full rounded-lg " +
                        (featured
                          ? "bg-amber-400 hover:bg-amber-500 text-slate-900"
                          : "bg-slate-900 hover:bg-slate-800 text-white")
                      }
                    >
                      {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                        <>Reserve founder pricing <ArrowRight className="h-4 w-4 ml-2" /></>
                      )}
                    </Button>
                  )
                ) : (
                  <Button
                    onClick={() => upgrade(p.id)}
                    disabled={busy !== null}
                    data-testid={`upgrade-${p.id}`}
                    className={
                      "w-full rounded-lg " +
                      (featured ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-blue-600 hover:bg-blue-700 text-white")
                    }
                  >
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <>
                        {currentPlan === "free" ? "Upgrade" : currentPlan === "pro" && p.id === "business" ? "Upgrade" : "Switch"} to {p.name}
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-400">
        {billingDisabled
          ? "Checkout temporarily unavailable. Contact support if this persists."
          : "Secure payments via Paddle. Cancel anytime. Prices in USD."}
      </p>
    </div>
  );
}
